import React from "react"
import {
  Box,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react"
import {
  DioboxEvent,
  FormType,
  ModalName,
  SubscriptionType,
} from "sharedTypes"
import * as Yup from "yup"
import SettingsSection from "components/settings/SettingsSection"
import Link from "components/elements/Link"
import UpgradeAlert from "components/elements/UpgradeAlert"
import { KIOSK_MODE_GUIDE_LINK } from "utilities/externalLinks"
import { eventFormsPath } from "utilities/routes"
import useModal from "services/useModal"
import useFeatureFlag from "services/featureFlags"
import ContainedForm from "components/ReactHookForm/ContainedForm"
import SwitchField from "components/ReactHookForm/SwitchField"
import Select from "components/ReactHookForm/Select"
import { useForms } from "queries/forms"
import KioskBackgroundModal from "pages/EventShow/Settings/KioskModals/KioskBackgroundModal"
import KioskBrandLogoModal from "pages/EventShow/Settings/KioskModals/KioskBrandLogoModal"
import KioskColorThemeModal from "pages/EventShow/Settings/KioskModals/KioskColorThemeModal"
import KioskGuestDisplayModal from "pages/EventShow/Settings/KioskModals/KioskGuestDisplayModal"
import KioskOnScreenTextModal from "pages/EventShow/Settings/KioskModals/KioskOnScreenTextModal"
import KioskPreviewCard from "./KioskPreview/KioskPreviewCard"

const WALK_IN_REGISTRATION_FORM_HELPER = "This form will seamlessly be integrated into the Kiosk experience, "
  + "allowing guests to complete registration directly on the device."

const KIOSK_AT_LEAST_ONE_ACTION_MESSAGE = "At least one kiosk action must be enabled: Scan QR Code or Walk-in Registration"
const KIOSK_WALK_IN_REQUIRES_FORM_MESSAGE = "Kiosk Walk-In Registration cannot be enabled without a Registration Form."
const KIOSK_WALK_IN_FORM_INVALID_MESSAGE = "Kiosk Walk-In Registration can only use a Registration Form for this event."

const GuestInteractionsForm = ({
  event,
  updateEvent,
}: {
  event: DioboxEvent
  updateEvent: (params: {}) => Promise<void>
}) => {
  const { isLoading: formsLoading, data: formsData } = useForms(event.id)
  const allForms = formsData?.data ?? []
  const kioskSelectableForms = allForms.filter((form) => form.type === FormType.Registration)
  const registrationFormOptions = kioskSelectableForms.map((form) => ({
    label: form.name || form.externalId,
    value: form.externalId,
  }))

  const registrationFormIdsKey = kioskSelectableForms.map((f) => f.externalId).join(",")

  const validationSchema = React.useMemo(
    () => Yup.object({
      kioskScanQrCodeEnabled: Yup.boolean(),
      kioskWalkInRegistrationEnabled: Yup.boolean(),
      kioskWalkInRegistrationFormExternalId: Yup.string(),
    })
      .test({
        name: "kiosk-actions-enabled",
        test(value, context) {
          if (value?.kioskScanQrCodeEnabled || value?.kioskWalkInRegistrationEnabled) {
            return true
          }

          return context.createError({
            path: "kioskScanQrCodeEnabled",
            message: KIOSK_AT_LEAST_ONE_ACTION_MESSAGE,
          })
        },
      })
      .test({
        name: "walk-in-registration-form",
        test(value, context) {
          if (value?.kioskWalkInRegistrationEnabled !== true) {
            return true
          }
          if (registrationFormOptions.length === 0) {
            return context.createError({
              path: "kioskWalkInRegistrationFormExternalId",
              message: KIOSK_WALK_IN_REQUIRES_FORM_MESSAGE,
            })
          }
          const extId = value?.kioskWalkInRegistrationFormExternalId?.trim()
          if (!extId) {
            return context.createError({
              path: "kioskWalkInRegistrationFormExternalId",
              message: KIOSK_WALK_IN_REQUIRES_FORM_MESSAGE,
            })
          }
          if (!registrationFormOptions.some((o) => o.value === extId)) {
            return context.createError({
              path: "kioskWalkInRegistrationFormExternalId",
              message: KIOSK_WALK_IN_FORM_INVALID_MESSAGE,
            })
          }

          return true
        },
      }),
    [kioskSelectableForms.length, registrationFormIdsKey],
  )

  return (
    <SettingsSection
      title="Guest Interactions"
      description="Select what guests can do when interacting with Kiosk Mode."
    >
      <ContainedForm
        values={{
          kioskScanQrCodeEnabled: event.kioskScanQrCodeEnabled ?? true,
          kioskWalkInRegistrationEnabled: event.kioskWalkInRegistrationEnabled ?? false,
          kioskWalkInRegistrationFormExternalId: event.kioskWalkInRegistrationForm?.externalId ?? "",
        }}
        name="Guest Interactions"
        validationSchema={validationSchema}
        onSubmit={async (v) => {
          const extId = v.kioskWalkInRegistrationFormExternalId?.trim()
          const walkInFormId = extId
            ? kioskSelectableForms.find((f) => f.externalId === extId)?.id ?? null
            : null

          await updateEvent({
            kioskScanQrCodeEnabled: v.kioskScanQrCodeEnabled,
            kioskWalkInRegistrationEnabled: v.kioskWalkInRegistrationEnabled,
            kioskWalkInRegistrationFormId: walkInFormId,
          })
        }}
      >
        {({
          control,
          watch,
          setValue,
          clearErrors,
          formState: { errors },
        }) => {
          const [scanEnabled, walkInEnabled, selectedExternalId] = watch([
            "kioskScanQrCodeEnabled",
            "kioskWalkInRegistrationEnabled",
            "kioskWalkInRegistrationFormExternalId",
          ])

          const ensureWalkInEnabled = () => {
            setValue("kioskWalkInRegistrationEnabled", true)
            if (!selectedExternalId && registrationFormOptions.length > 0) {
              setValue("kioskWalkInRegistrationFormExternalId", registrationFormOptions[0].value)
            }
          }

          return (
            <>
              <Box mb={8}>
                <SwitchField
                  name="kioskScanQrCodeEnabled"
                  control={control}
                  label="Scan QR Code"
                  fontSize="lg"
                  size="lg"
                  mb={2}
                  dataTestId="kiosk-scan-qr-code-switch"
                  onChange={(e) => {
                    const nextEnabled = e.target.checked
                    if (!nextEnabled && !walkInEnabled) {
                      if (formsLoading || registrationFormOptions.length === 0) {
                        setValue("kioskScanQrCodeEnabled", true)

                        return
                      }
                      ensureWalkInEnabled()
                    }
                  }}
                />
                <Text>
                  Allow guests to scan their QR code to check in, print a badge, or view their seat.
                </Text>
                {errors.kioskScanQrCodeEnabled?.message && (
                  <Text mt={2} color="red.500" fontSize="sm" role="alert">
                    {errors.kioskScanQrCodeEnabled.message}
                  </Text>
                )}
              </Box>

              <Box>
                <SwitchField
                  name="kioskWalkInRegistrationEnabled"
                  control={control}
                  label="Walk-In Registration"
                  fontSize="lg"
                  size="lg"
                  mb={2}
                  dataTestId="kiosk-walk-in-registration-switch"
                  disabled={formsLoading
                    || (!walkInEnabled && registrationFormOptions.length === 0)}
                  onChange={(e) => {
                    const nextEnabled = e.target.checked
                    if (!nextEnabled && !scanEnabled) {
                      setValue("kioskScanQrCodeEnabled", true)
                    }
                    if (!nextEnabled) {
                      clearErrors("kioskWalkInRegistrationFormExternalId")

                      return
                    }

                    if (!selectedExternalId && registrationFormOptions.length > 0) {
                      setValue("kioskWalkInRegistrationFormExternalId", registrationFormOptions[0].value)
                    }
                  }}
                />
                <Text mb={3}>
                  Allow guests to register directly on the device using a selected form.
                </Text>
                {registrationFormOptions.length === 0 ? (
                  <Text mt={2} color="gray.600" fontSize="sm">
                    To enable,
                    {" "}
                    <Link to={eventFormsPath(event.id)}>go to Forms</Link>
                    {" "}
                    → Create New Registration Form.
                  </Text>
                ) : (
                  <Select
                    name="kioskWalkInRegistrationFormExternalId"
                    control={control}
                    includeBlank
                    blankLabel="Select Form"
                    disabled={!walkInEnabled || formsLoading}
                    options={registrationFormOptions}
                    vertical
                    helperText={WALK_IN_REGISTRATION_FORM_HELPER}
                    mb={0}
                  />
                )}
                {errors.kioskWalkInRegistrationFormExternalId?.message && (
                  <Text mt={2} color="red.500" fontSize="sm" role="alert">
                    {errors.kioskWalkInRegistrationFormExternalId.message}
                  </Text>
                )}
              </Box>
            </>
          )
        }}
      </ContainedForm>
    </SettingsSection>
  )
}

type Props = {
  kioskModeEnabled: boolean
  event: DioboxEvent
  updateEvent: (params: {}) => Promise<void>
}

const KioskMode = ({ kioskModeEnabled, event, updateEvent }: Props) => {
  const { kioskForms: kioskActionsFeatureEnabled } = useFeatureFlag()
  const showModal = useModal()

  const backgroundModal = useDisclosure()
  const brandLogoModal = useDisclosure()
  const colorThemeModal = useDisclosure()
  const guestDisplayModal = useDisclosure()
  const onScreenTextModal = useDisclosure()

  const modalCommon = {
    event,
    updateEvent,
  }

  return (
    <>
      <Stack spacing={16}>
        <SettingsSection
          title="Kiosk Mode"
          description={(
            <>
              <Text>
                Kiosk Mode enables a self-service registration and check-in experience.
                {" "}
                Guests can scan their QR codes or complete a walk-in registration upon arrival.
                {" "}
                It runs on the Diobox iOS app and supports full customization of branding,
                {" "}
                on-screen messaging,
                {" "}
                and guest information display.
              </Text>
              <Text>
                To learn more, see the{" "}
                <Link isExternal href={KIOSK_MODE_GUIDE_LINK}>Kiosk Mode Guide</Link>.
              </Text>
            </>
          )}
        >
          {!kioskModeEnabled && (
            <UpgradeAlert
              text="This feature requires an Enterprise Subscription."
              defaultSelectedPlan={SubscriptionType.Enterprise}
            />
          )}
        </SettingsSection>

        {kioskActionsFeatureEnabled && (
          <GuestInteractionsForm
            event={event}
            updateEvent={updateEvent}
          />
        )}

        <SettingsSection
          title="Branding & Customization"
          description="Control the visual style and branding used throughout Kiosk Mode."
        >
          <Stack spacing={16}>
            <KioskPreviewCard
              event={event}
              onOpenBackground={backgroundModal.onOpen}
              onOpenBrandLogo={brandLogoModal.onOpen}
              onOpenEventTitle={() => {
                showModal(ModalName.PublicEventTitle, {
                  updateEvent,
                })
              }}
              onOpenColorTheme={colorThemeModal.onOpen}
              onOpenGuestDisplay={guestDisplayModal.onOpen}
              onOpenOnScreenText={onScreenTextModal.onOpen}
            />
          </Stack>
        </SettingsSection>
      </Stack>

      <KioskBackgroundModal
        isOpen={backgroundModal.isOpen}
        onClose={backgroundModal.onClose}
        {...modalCommon}
      />

      <KioskBrandLogoModal
        isOpen={brandLogoModal.isOpen}
        onClose={brandLogoModal.onClose}
        {...modalCommon}
      />

      <KioskColorThemeModal
        isOpen={colorThemeModal.isOpen}
        onClose={colorThemeModal.onClose}
        {...modalCommon}
      />

      <KioskGuestDisplayModal
        isOpen={guestDisplayModal.isOpen}
        onClose={guestDisplayModal.onClose}
        {...modalCommon}
      />

      <KioskOnScreenTextModal
        isOpen={onScreenTextModal.isOpen}
        onClose={onScreenTextModal.onClose}
        event={event}
      />
    </>
  )
}

export default KioskMode
